import { useAuth, usePremiumStatus } from '../hooks';
import { premiumFeatures } from '../utils';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../amplify/data/resource';
import outputs from '../amplify_outputs.json';
import './AccountPage.css';

const client = generateClient<Schema>();

// Get the Stripe API URL from outputs
const stripeApiUrl = (outputs as { custom?: { stripeApiUrl?: string } }).custom?.stripeApiUrl || '';

function AccountPage() {
  const { user, signOut } = useAuth();
  const { tier, isPremium, isLoading, userId } = usePremiumStatus();
  const [searchParams] = useSearchParams();
  const [message, setMessage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [stripeCustomerId, setStripeCustomerId] = useState<string | null>(null);

  // Check for success/cancel from Stripe redirect
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setMessage('Payment successful! Your account has been upgraded to Premium.');
    } else if (searchParams.get('canceled') === 'true') {
      setMessage('Payment was canceled.');
    }
  }, [searchParams]);

  // Fetch stripe customer ID for portal access
  useEffect(() => {
    async function fetchCustomerId() {
      if (userId) {
        try {
          const { data: profile } = await client.models.UserProfile.get({ owner: userId });
          if (profile?.stripeCustomerId) {
            setStripeCustomerId(profile.stripeCustomerId);
          }
        } catch (error) {
          console.error('Error fetching profile:', error);
        }
      }
    }
    fetchCustomerId();
  }, [userId]);

  const handleUpgrade = async () => {
    if (!user || !userId) return;
    
    setIsProcessing(true);
    try {
      const session = await fetchAuthSession();
      const email = user.signInDetails?.loginId || '';

      const response = await fetch(`${stripeApiUrl}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.tokens?.idToken?.toString()}`,
        },
        body: JSON.stringify({ userId, email }),
      });

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        setMessage('Failed to create checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      setMessage('An error occurred. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!stripeCustomerId) return;
    
    setIsProcessing(true);
    try {
      const session = await fetchAuthSession();

      const response = await fetch(`${stripeApiUrl}/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.tokens?.idToken?.toString()}`,
        },
        body: JSON.stringify({ stripeCustomerId }),
      });

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        setMessage('Failed to open subscription portal. Please try again.');
      }
    } catch (error) {
      console.error('Error opening portal:', error);
      setMessage('An error occurred. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="account-page">
        <div className="account-loading">Loading account...</div>
      </div>
    );
  }

  return (
    <div className="account-page">
      <h1>My Account</h1>

      {message && (
        <div className={`message ${searchParams.get('success') ? 'success' : 'info'}`}>
          {message}
        </div>
      )}
      
      <section className="account-section">
        <h2>Profile</h2>
        <div className="profile-info">
          <div className="info-row">
            <span className="label">Email:</span>
            <span className="value">{user?.signInDetails?.loginId || 'N/A'}</span>
          </div>
          <div className="info-row">
            <span className="label">User ID:</span>
            <span className="value">{user?.userId || 'N/A'}</span>
          </div>
        </div>
      </section>

      <section className="account-section">
        <h2>Subscription</h2>
        <div className="subscription-info">
          <div className="tier-badge" data-tier={tier.toLowerCase()}>
            {tier}
          </div>
          
          {isPremium ? (
            <div className="subscription-details">
              <p>You have access to all premium features!</p>
              <button 
                className="btn btn-secondary"
                onClick={handleManageSubscription}
                disabled={isProcessing || !stripeCustomerId}
              >
                {isProcessing ? 'Loading...' : 'Manage Subscription'}
              </button>
            </div>
          ) : (
            <div className="subscription-details">
              <p>Upgrade to Premium to unlock additional coverages and features.</p>
              {/*
                * From the same source as the pricing page. This list used to be written separately
                * and disagreed with it — and included "Priority support", which the product does not
                * offer, and "Advanced route combinations", which is not gated.
                */}
              <ul className="premium-features">
                {premiumFeatures().map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
              <button 
                className="btn btn-primary"
                onClick={handleUpgrade}
                disabled={isProcessing}
              >
                {isProcessing ? 'Loading...' : 'Upgrade to Premium'}
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="account-section">
        <h2>Account Actions</h2>
        <button 
          className="btn btn-danger"
          onClick={signOut}
        >
          Sign Out
        </button>
      </section>
    </div>
  );
}

export default AccountPage;
